import {
  PrinterDuty,
  PrinterTemplate,
  UsbAndroidPrinterConfig,
  UsbAndroidPrintersConfig,
} from '../types';
import { EscPosPrinter } from '@/plugins/escpos-printer';
import { encodeTemplateToEscPos, uint8ToBase64 } from '../escpos/encodeTemplateToEscPos';
import { readPrinterSetting, writePrinterSetting } from '../printerSettingsStorage';

const normalizeUsbAndroidPrinterConfig = (
  printer: Partial<UsbAndroidPrinterConfig>
): UsbAndroidPrinterConfig => {
  return {
    id: printer.id ?? crypto.randomUUID(),
    name: printer.name ?? 'Built-in USB Printer',
    duties: printer.duties ?? ['cashier'],
    vendorId: printer.vendorId,
    productId: printer.productId,
    codePage: printer.codePage ?? 'cp858',
    openCashDrawer: printer.openCashDrawer ?? false,
    printDispatchCopy: printer.printDispatchCopy ?? false,
    language: printer.language ?? 'es',
    currency: printer.currency ?? 'USD',
  };
};

export const createDefaultUsbAndroidPrinterConfig = (
  overrides: Partial<UsbAndroidPrinterConfig> = {}
): UsbAndroidPrinterConfig => {
  return normalizeUsbAndroidPrinterConfig({
    id: crypto.randomUUID(),
    duties: ['cashier'],
    ...overrides,
  });
};

export const getUsbAndroidPrintersConfig = (): UsbAndroidPrintersConfig => {
  try {
    const dbConfig = readPrinterSetting<UsbAndroidPrintersConfig>('usbAndroidPrintersConfig');
    if (dbConfig) {
      return {
        enabled: dbConfig.enabled ?? false,
        printers: (dbConfig.printers ?? []).map(normalizeUsbAndroidPrinterConfig),
      };
    }
  } catch (error) {
    console.warn('Failed to parse USB Android printer config:', error);
  }

  return { enabled: false, printers: [] };
};

export const saveUsbAndroidPrintersConfig = async (
  config: UsbAndroidPrintersConfig
): Promise<void> => {
  await writePrinterSetting('usbAndroidPrintersConfig', config);
};

const getPrintersForDuty = (duty: PrinterDuty): UsbAndroidPrinterConfig[] => {
  const config = getUsbAndroidPrintersConfig();
  if (!config.enabled || config.printers.length === 0) return [];
  return config.printers.filter((printer) => printer.duties.includes(duty));
};

export const listUsbAndroidDevices = async () => {
  const result = await EscPosPrinter.listUsbDevices();
  return result.devices ?? [];
};

export const requestUsbAndroidPermission = async (vendorId?: number, productId?: number) => {
  return EscPosPrinter.requestUsbPermission({ vendorId, productId });
};

export const sendToUsbAndroidPrinter = async (
  template: PrinterTemplate,
  printerId: string,
  options?: { openCashDrawer?: boolean },
): Promise<boolean> => {
  const config = getUsbAndroidPrintersConfig();
  if (!config.enabled) return false;

  const printer = config.printers.find((item) => item.id === printerId);
  if (!printer) {
    throw new Error(`USB Android printer not found: ${printerId}`);
  }

  const bytes = encodeTemplateToEscPos(template, {
    openCashDrawer: options?.openCashDrawer ?? printer.openCashDrawer,
    codePage: printer.codePage,
  });
  const dataBase64 = uint8ToBase64(bytes);

  await EscPosPrinter.printRawUsb({
    dataBase64,
    vendorId: printer.vendorId,
    productId: printer.productId,
  });
  return true;
};

export const sendToUsbAndroidPrinterByDuty = async (
  template: PrinterTemplate,
  duty: PrinterDuty
): Promise<boolean> => {
  const printers = getPrintersForDuty(duty);
  if (printers.length === 0) {
    console.warn(`No USB Android printer assigned for duty: ${duty}`);
    return false;
  }

  const results = await Promise.all(
    printers.map((printer) => {
      console.log(`[USB Android] Enviando documento a la impresora: ${printer.name} (Responsabilidad: ${duty})`);
      return sendToUsbAndroidPrinter(template, printer.id);
    })
  );

  return results.some((success) => success);
};
